import fetchTree, {
    depends,
    loader,
    group,
    virtual,
    preload,
} from 'fetch-tree';
import React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';

import Collection from './section/collection.jsx';
import ContentPreview, {
    resources as contentPreviewResources,
} from './content_preview.jsx';
import EditCollectionDialog from './edit_collection_dialog.jsx';
import VerticalDrawer from './vertical_drawer.jsx';

import { sectionContentLoader } from '../resources/content';
import { selectCollections } from '../selectors/section';
import { isInstructorInSection } from '../selectors/current_user';
import { countUnansweredQuestions } from '../selectors/markup';
import { fetchUnansweredCount } from '../actions/markup';
import { selectNonCollectionContentForSection } from '../selectors/content';
import { deleteCollection } from '../actions/collections.js';
// TODO: confirm this doesn't need code splitting
import tabbable from 'tabbable';
import { setOrigin } from 'actions/content';
import { addAural } from 'actions/aural_notification';
import { push } from 'react-router-redux';

class Section extends React.Component {
    static displayName = 'Section';

    static propTypes = {
        query: PropTypes.object.isRequired,
        collections: PropTypes.array.isRequired,
        content: PropTypes.array.isRequired,
        isInstructor: PropTypes.bool.isRequired,
        section: PropTypes.any.isRequired,
        analyticsMode: PropTypes.string.isRequired,
        unansweredCollectionCounts: PropTypes.objectOf(PropTypes.number)
            .isRequired,
        deleteCollection: PropTypes.func.isRequired,
        isConnected: PropTypes.bool.isRequired,
        setOrigin: PropTypes.func.isRequired,
        addAural: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
    };

    // focusses on first tabbable element in a collection
    // needs to wait until after the component updates to set focus on first collection item
    componentDidUpdate() {
        const container = document.querySelector('.collections-wrapper');

        if (container !== null) {
            const tabbableNodes = tabbable(container);
            if (tabbableNodes.length > 0) {
                tabbableNodes[0].focus();
            }
        }
    }

    renderCollectionContent(key) {
        const collection = this.props.collections.find(c => c.name === key);

        if (collection) {
            const pathname = `term/${this.props.section.term_id}`;

            return (
                <div className="collections-wrapper">
                    <h2>{collection.name}</h2>
                    <div className="materials">
                        {this.renderContentPreviews(collection.content)}
                    </div>
                    <button
                        onClick={() => this.props.push(pathname)}
                        className="close"
                    >
                        <span className="aural">Close collection</span>
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
            );
        }
    }
    renderCollections(collections) {
        const {
            section,
            isInstructor,
            unansweredCollectionCounts,
            setOrigin,
            push,
            addAural,
        } = this.props;
        const { deleteCollection, query, isConnected } = this.props;

        return R.map(collection => {
            return (
                <Collection
                    isConnected={isConnected}
                    key={collection.name}
                    canEdit={isInstructor}
                    collection={collection}
                    section={section}
                    deleteCollection={deleteCollection}
                    unansweredCount={
                        unansweredCollectionCounts[collection.name]
                    }
                    query={query}
                    setOrigin={setOrigin}
                    push={push}
                    addAural={addAural}
                />
            );
        }, collections);
    }

    renderContentPreviews(allContent) {
        return allContent.map(content => {
            return (
                <ContentPreview
                    key={content.id}
                    contentId={content.id}
                    analyticsMode={this.props.analyticsMode}
                    push={this.props.push}
                />
            );
        });
    }

    renderDialog(name) {
        const { section, content, collections } = this.props;
        const collection = R.find(c => c.name === name, collections);

        if (collection) {
            const tmp = R.compose(
                // 5. Sort the content so it doesn't shift
                R.sortBy(R.prop('title')),
                // 4. Filter the set to unique content (content may exist in
                // multiple collections)
                R.uniqBy(R.prop('id')),
                // 3. Add it to the list of content that isn't in a collection
                R.concat(content),
                // 2. flatten this list
                R.flatten,
                // 1. pluck each collection's set of content
                R.map(R.prop('content'))
            );

            const allContent = tmp(collections);

            return (
                <EditCollectionDialog
                    section={section}
                    content={allContent}
                    collection={collection}
                />
            );
        }
    }

    render() {
        const { section, content, collections, query } = this.props;
        const {
            instructor_names,
            course_title,
            course_subject,
            course_catalog_number,
            class_number,
        } = section;

        let editDialog;
        let openCollection;
        if (parseInt(query.section, 10) === section.id) {
            openCollection = query.collection;
            if (query.editCollection) {
                editDialog = this.renderDialog(query.editCollection);
            }
        }

        return (
            <div className="content">
                <div className="course-title">
                    <div className="section-info">
                        <span className="section-number">
                            {course_subject + ' - ' + course_catalog_number}
                        </span>
                        Section {class_number}
                    </div>
                    <h2>
                        <span className="aural"> Course: </span>
                        {course_title}
                    </h2>
                    <div className="instructor-tally">
                        <span className="kicker">Instructors</span>
                        {instructor_names}
                    </div>
                </div>
                {editDialog}
                <VerticalDrawer
                    selected={openCollection}
                    expandCallback={this.renderCollectionContent.bind(this)}
                >
                    {this.renderCollections(collections)}
                    {this.renderContentPreviews(content)}
                </VerticalDrawer>
                <div className="collections" />
                <div className="materials js-section-view-container clearfix" />
            </div>
        );
    }
}

export const resources = group({
    collections: depends([depends.prop('section')], selectCollections),
    isInstructor: depends([depends.prop('section')], isInstructorInSection),
    allContent: depends([depends.prop('section')], sectionContentLoader),
    content: depends(
        [depends.prop('section'), 'allContent'],
        (state, section) => selectNonCollectionContentForSection(state, section)
    ),
    flatContent: virtual(
        depends(['allContent', 'collections'], (state, content, collections) =>
            collections.reduce((content, collection) => {
                return [...content, ...collection.content];
            }, [])
        )
    ),
    unansweredCollectionCounts: depends(
        ['flatContent', 'isInstructor', 'collections'],
        loader({
            lazy: true,
            id: flatContent => {
                const ids = flatContent.map(c => c.id).join(':');
                return `unanswered-${ids}`;
            },
            action(flatContent, isInstructor, collections) {
                return dispatch => {
                    if (!isInstructor) {
                        return Promise.resolve(null);
                    }

                    return Promise.all(
                        flatContent.map(c => dispatch(fetchUnansweredCount(c)))
                    );
                };
            },
            selector(state, flatContent, isInstructor, collections) {
                // Students don't have unanswered counts and this can bail early
                // if there is no content.
                if (!isInstructor || flatContent.length === 0) {
                    return collections.reduce((memo, { name }) => {
                        memo[name] = 0;
                        return memo;
                    }, {});
                }

                return collections.reduce((countMemo, collection) => {
                    countMemo[collection.name] = collection.content.reduce(
                        (count, content) => {
                            return (
                                count + countUnansweredQuestions(state, content)
                            );
                        },
                        0
                    );
                    return countMemo;
                }, {});
            },
        })
    ),
    contentPreviews: depends(
        [depends.prop('analyticsMode'), 'allContent'],
        preload((useProps, analyticsMode, allContent) =>
            allContent.map(content =>
                useProps(
                    {
                        contentId: content.id,
                        analyticsMode,
                    },
                    contentPreviewResources
                )
            )
        )
    ),
});

export default fetchTree({
    component: Section,
    mapDispatchToProps: {
        deleteCollection,
        setOrigin,
        addAural,
        push,
    },
    resources,
});



// WEBPACK FOOTER //
// ./src/components/section.jsx